import React, { useState, useMemo } from "react";
import DashboardCardWrapper from "../../common/DashboardCardWrapper";
import DashboardCardHeader from "../../common/DashboardCardHeader";
import BorderedIconWrapper from "../../common/BorderIconWrapper";
import { CardContent } from "../../ui/card";
import AdtAlertsIcon from "@/assets/svg/adtAlerts.svg";
import { DataTable } from "@/components/common/Table";
import { columns } from "./adtAlertsColumn";
import { getBaseUrl } from "@/utils/urls";
import { RootState } from "@/store/store";
import { useSelector } from "react-redux";
import AdtAlertsDarkIcon from "@/assets/svg/adtAlertsDarkIcon.svg";
import { Button } from "@/components/ui/button";
import AdtAlertsDialog from "./AdtAlertsDialog";
import { useDashboardCardFavorite } from "@/hooks/useDashboardCardFavorite";
import { Typography } from "@/components/ui/typography";
import { useFetchCard } from "@/hooks/useFetchCard";

interface AdtAlert {
  id: number;
  eventdate: string;
  eventType: string;
  facility: string;
}

interface AdtAlertsResponse {
  status: number;
  data: {
    eventDate: string;
    eventType: string;
    eventFacility: string;
  }[];
}

interface AdtAlertsCardProps {
  shouldBlink?: boolean;
}

const AdtAlertsCard: React.FC<AdtAlertsCardProps> = ({
  shouldBlink = false,
}) => {
  const { cardVariant, isFavourite, toggleFavourite } =
    useDashboardCardFavorite("ADT_ALERTS");
  const [dialogOpen, setDialogOpen] = useState(false);

  const memberId =
    useSelector((state: RootState) => state.search.selectedPatient?.memberId) ||
    "";

  const {
    data: adtAlertsResponse,
    loading,
    error,
    isErrorRed,
  } = useFetchCard<AdtAlertsResponse["data"]>({
    endpoint: `${getBaseUrl()}/adt-alerts`,
    memberId,
    method: "POST",
    requestBody: { view: "card" },
    processData: (rawData: unknown) => (rawData as AdtAlertsResponse).data,
  });

  const alertsData = useMemo(() => {
    if (!adtAlertsResponse || adtAlertsResponse.length === 0) return [];
    return adtAlertsResponse.map((item, index) => ({
      id: index + 1,
      eventdate: item.eventDate,
      eventType: item.eventType,
      facility: item.eventFacility,
    }));
  }, [adtAlertsResponse]);

  const handleViewAll = () => {
    setDialogOpen(true);
  };

  return (
    <>
      <div className='bg-white dark:bg-gray-800 rounded-lg shadow-md h-fit'>
        <DashboardCardWrapper variant={cardVariant} shouldBlink={shouldBlink}>
          <DashboardCardHeader
            icon={
              <BorderedIconWrapper>
                <img
                  src={AdtAlertsIcon}
                  alt='AdtAlertsIcon'
                  className='w-4 h-4 block dark:hidden'
                />
                <img
                  src={AdtAlertsDarkIcon}
                  alt='AdtAlertsDarkIcon'
                  className='w-4 h-4 hidden dark:block'
                />
              </BorderedIconWrapper>
            }
            title='ADT Alerts'
            className='h-[56px]'
            showStar={true}
            button={
              <Button
                variant='button'
                className='!h-[28px] !px-3 !py-1 !text-sm mb-[2px]'
                onClick={handleViewAll}
                disabled={loading || !!error || alertsData.length === 0}>
                View all
              </Button>
            }
            isFavourite={isFavourite}
            onToggleFavourite={toggleFavourite}
          />
          <CardContent className='p-4 lg:h-[312px] md:h-[558px]'>
            {error ? (
              <div className='flex items-center justify-center lg:h-[312px] md:h-[523px] sm:h-full'>
                <Typography
                  variant='medium-14'
                  className={`${isErrorRed
                    ? "text-red-500 dark:text-red-400"
                    : "text-grey-600 dark:text-grey-500"
                    } font-medium`}>
                  {error}
                </Typography>
              </div>
            ) : (
              <div className='max-h-[288px] md:max-h-[419px] lg:max-h-[288px]'>
                <DataTable<AdtAlert>
                  data={{
                    data: alertsData,
                    total: alertsData.length,
                  }}
                  columns={columns}
                  height='100%'
                  isLoading={loading}
                  skeletonCount={6}
                />
              </div>
            )}
          </CardContent>
        </DashboardCardWrapper>
      </div>
      {/* ADT Alerts Dialog */}
      <AdtAlertsDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        memberId={memberId}
      />
    </>
  );
};

export default AdtAlertsCard;
